'use client';

import { ensureClient, slugify } from './util';
import type { Tables } from '@/types/supabase';

export type PlayerRecord = Pick<
  Tables<'player'>,
  | 'id'
  | 'user_id'
  | 'first_name'
  | 'last_name'
  | 'gender'
  | 'nationality'
  | 'hand_preference'
  | 'profile_photo'
  | 'username'
  | 'created_at'
  | 'updated_at'
>;

export type BasicPlayer = Pick<
  Tables<'player'>,
  'id' | 'first_name' | 'last_name' | 'username' | 'current_rating' | 'profile_photo'
>;

export const userModel = {
  async getByUsername(username: string) {
    const client = ensureClient();
    const { data, error } = await client
      .from('player')
      .select(
        'id, user_id, first_name, last_name, gender, nationality, hand_preference, profile_photo, username, created_at, updated_at',
      )
      .eq('username', username)
      .maybeSingle();
    if (error) throw error;
    return (data as PlayerRecord) || null;
  },

  async getById(id: string) {
    const client = ensureClient();
    const { data, error } = await client
      .from('player')
      .select(
        'id, user_id, first_name, last_name, gender, nationality, hand_preference, profile_photo, username, created_at, updated_at',
      )
      .eq('id', id)
      .maybeSingle();
    if (error) throw error;
    return data as PlayerRecord | null;
  },

  async getByUserId(userId: string) {
    const client = ensureClient();
    const { data, error } = await client
      .from('player')
      .select(
        'id, user_id, first_name, last_name, gender, nationality, hand_preference, profile_photo, username, created_at, updated_at',
      )
      .eq('user_id', userId)
      .maybeSingle();
    if (error) throw error;
    return (data as PlayerRecord) || null;
  },

  // Future: helpers for username-based lookups once types are regenerated
  async listAllBasicPlayers(): Promise<
    Array<Pick<Tables<'player'>, 'id' | 'first_name' | 'last_name'>>
  > {
    const client = ensureClient();
    const { data, error } = await client
      .from('player')
      .select('id, first_name, last_name')
      .order('first_name');
    if (error) throw error;
    return (data ?? []) as Array<Pick<Tables<'player'>, 'id' | 'first_name' | 'last_name'>>;
  },

  async saveForUser(
    userId: string,
    payload: Omit<PlayerRecord, 'id' | 'user_id' | 'username' | 'created_at' | 'updated_at'>,
  ) {
    const client = ensureClient();
    const existing = await this.getByUserId(userId);
    const record = {
      ...payload,
      user_id: userId,
    } as const;
    if (existing?.id) {
      const { error } = await client.from('player').update(record).eq('id', existing.id);
      if (error) throw error;
      return { ...existing, ...record, id: existing.id } as PlayerRecord;
    }
    const { data, error } = await client.from('player').insert(record).select('*').maybeSingle();
    if (error) throw error;
    return data as PlayerRecord;
  },

  async getAffiliatedClubs(playerId: string) {
    const client = ensureClient();
    const { data: clubRows, error: clubErr } = await client
      .from('player_location')
      .select('*, location!inner(*)')
      .eq('player_id', playerId)
      // Exclude clubs the member has been removed from (soft-archived link) so a
      // removed member's public profile stops advertising the affiliation.
      .neq('status', 'archived')
      .eq('location.type', 'club');
    if (clubErr) throw clubErr;
    return (clubRows ?? []).map((row) => row.location) as Array<Tables<'location'>>;
  },

  async getAffiliatedLocations(playerId: string) {
    const client = ensureClient();
    const { data: locationRows, error: locationErr } = await client
      .from('player_location')
      .select('*, location!inner(*)')
      .eq('player_id', playerId)
      .neq('status', 'archived')
      .neq('location.type', 'club');
    if (locationErr) throw locationErr;
    return (locationRows ?? []).map((row) => row.location) as Array<Tables<'location'>>;
  },

  createSlugFromEmail(email: string): string {
    const name = email.split('@')[0] || 'me';
    return slugify(name);
  },

  /**
   * Get the current user's profile URL
   * @param locale - The current locale
   * @param userId - The user ID (optional, will be fetched from session if not provided)
   * @returns The profile URL for the current user
   */
  async getCurrentUserProfileUrl(locale: string, userId?: string): Promise<string> {
    if (!userId) {
      // Get user ID from session
      const { authModel } = await import('./authModel');
      const session = await authModel.getSession();
      if (!session?.user?.id) {
        throw new Error('User not authenticated');
      }
      userId = session.user.id;
    }

    // Try to get user record with username
    const userRecord = await this.getByUserId(userId);
    if (userRecord?.username) {
      return `/${locale}/user/${userRecord.username}`;
    }

    // No player record (admin-only user) — no profile page
    return '';
  },

  // Temporary: return dummy match history until DB schema is ready
  async getMatchHistoryPlaceholder(playerIdOrSlug: string) {
    // Use a deterministic seed based on input for stable demo data
    const names = [
      'Michael Flannagan',
      'Krishna Viswanathan',
      'Adam Soonios',
      'Ignacio Basanes',
      'Ambrose Tho',
      'Cameron Hill',
      'Stanley Mok',
    ];
    const systems = ['Par 15, Best of 5', 'Par 11, Best of 5'];
    const results = ['3-0', '3-1', '3-2', '2-3', '1-3', '0-3'];
    const today = new Date();
    const rows = Array.from({ length: 10 }).map((_, i) => {
      const d = new Date(today);
      d.setDate(d.getDate() - i * 7);
      const opp = names[i % names.length];
      const res = results[i % results.length];
      const sys = systems[i % systems.length];
      return {
        id: `${playerIdOrSlug}-${i}`,
        dateISO: d.toISOString().slice(0, 10),
        opponent: opp,
        system: sys,
        result: res,
        venue: i % 2 === 0 ? 'Home' : 'Away',
      } as const;
    });
    return rows;
  },
};
